import { Alert, Descriptions, Input, Modal, Typography } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { kindLabel, nomzodXulosasi, type RetentionRun } from "../types";

const { Paragraph, Text } = Typography;

/**
 * Sabab uchun eng kichik uzunlik.
 *
 * ⚠️⚠️ **Backend bilan AYNAN BIR XIL** — `RetentionReasonSchema`
 * (`retention.schemas.ts`) `.trim().min(5)` qo'llaydi. Bu yerda
 * kichikroq chegara qo'yilsa, tugma yoqilib turib server 422 qaytarardi
 * va foydalanuvchi xatoni faqat bosgandan KEYIN ko'rardi.
 */
export const SABAB_MIN = 5;

export interface ConfirmModalProps {
	open: boolean;
	run: RetentionRun | null;
	/** `confirm` — o'chirishni tasdiqlash; `cancel` — yugurishni bekor qilish. */
	mode: "confirm" | "cancel";
	loading?: boolean;
	onCancel: () => void;
	onSubmit: (reason: string) => void;
}

/**
 * Tasdiqlash / bekor qilish modali.
 *
 * ⚠️⚠️ **QAYTARIB BO'LMAYDIGAN AMAL.** Modal buni ANIQ aytadi
 * (`Alert type="error"`) va sabab MAJBURIY — bo'sh yoki qisqa sabab
 * bilan tugma O'CHIRILGAN.
 *
 * ⚠️ Tasdiqlash tugmasi `danger` rangida va matni ANIQ:
 * «Ha, o'chirilsin» — «OK» emas. Umumiy «OK» matni admin nimaga
 * rozilik berayotganini yashirardi.
 *
 * ⚠️ Modal nima o'chirilishini QAYTA sanaydi («3 ta partition,
 * ~12.4 GiB») — admin tasdiqlashdan oldin oxirgi marta ko'radi.
 * Raqam kartadagi bilan AYNAN bir manbadan (`nomzodXulosasi`), ya'ni
 * ikkalasi hech qachon ajralmaydi.
 */
export function ConfirmModal({
	open,
	run,
	mode,
	loading = false,
	onCancel,
	onSubmit,
}: ConfirmModalProps) {
	const { t } = useTranslation("retention");
	const [reason, setReason] = useState("");

	// Modal har ochilganda sabab tozalanadi — aks holda oldingi
	// yugurishning sababi yangisiga jimgina ko'chib o'tardi.
	useEffect(() => {
		if (open) {
			setReason("");
		}
	}, [open]);

	const tasdiqlash = mode === "confirm";
	const yetarli = reason.trim().length >= SABAB_MIN;

	return (
		<Modal
			open={open}
			title={tasdiqlash ? t("confirmModal.titleConfirm") : t("confirmModal.titleCancel")}
			okText={tasdiqlash ? t("confirmModal.okConfirm") : t("confirmModal.okCancel")}
			cancelText={t("confirmModal.cancelText")}
			okButtonProps={{ danger: true, disabled: !yetarli, loading }}
			onOk={() => onSubmit(reason.trim())}
			onCancel={onCancel}
			destroyOnHidden
		>
			{tasdiqlash ? (
				<Alert
					type="error"
					showIcon
					className="!mb-4"
					title={t("confirmModal.alertConfirmTitle")}
					description={t("confirmModal.alertConfirmDescription")}
				/>
			) : (
				<Alert
					type="info"
					showIcon
					className="!mb-4"
					title={t("confirmModal.alertCancelTitle")}
					description={t("confirmModal.alertCancelDescription")}
				/>
			)}

			{run ? (
				<Descriptions size="small" column={1} bordered className="!mb-4">
					<Descriptions.Item label={t("confirmModal.kindLabel")}>
						{kindLabel(t, run.kind)}
					</Descriptions.Item>
					<Descriptions.Item
						label={tasdiqlash ? t("confirmModal.deletesLabel") : t("confirmModal.candidatesLabel")}
					>
						<Text strong>{nomzodXulosasi(t, run)}</Text>
					</Descriptions.Item>
					<Descriptions.Item label={t("confirmModal.cutoffLabel")}>
						{new Date(run.cutoffAt).toLocaleString("uz-UZ")}
					</Descriptions.Item>
				</Descriptions>
			) : null}

			<Paragraph className="!mb-1">
				<Text strong>{t("confirmModal.reasonHeading")}</Text> <Text type="danger">*</Text>
			</Paragraph>
			<Paragraph type="secondary" className="!mb-2 !text-xs">
				{t("confirmModal.reasonHint")}
			</Paragraph>
			<Input.TextArea
				value={reason}
				onChange={(event) => setReason(event.target.value)}
				rows={3}
				maxLength={500}
				showCount
				placeholder={t("confirmModal.reasonPlaceholder")}
				aria-label={t("confirmModal.reasonAriaLabel")}
			/>
			{yetarli ? null : (
				<Text type="secondary" className="!text-xs">
					{t("confirmModal.reasonTooShort", { min: SABAB_MIN })}
				</Text>
			)}
		</Modal>
	);
}
